import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, PlayCircle } from 'lucide-react';
import { Button } from '../ui/button';
import TutorialModal from './TutorialModal';
import { TUTORIAL_STORAGE } from '../../config/tutorial';
import { fireTutorialEvent, TUTORIAL_EVENTS } from '../../lib/tutorialAnalytics';

/**
 * WelcomeTutorialCard
 *
 * Non-blocking welcome card shown on the dashboard for first-time users.
 * Two buttons: "Watch Tutorial" / "Skip for now". State is persisted in
 * localStorage so the card doesn't nag returning users. Users can always
 * reopen the tutorial from the Help menu or the dashboard launcher card.
 */
export default function WelcomeTutorialCard() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(TUTORIAL_STORAGE.DISMISSED) === 'true';
      const completed = localStorage.getItem(TUTORIAL_STORAGE.COMPLETED) === 'true';
      if (!dismissed && !completed) setVisible(true);
    } catch { /* ignore */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(TUTORIAL_STORAGE.DISMISSED, 'true'); } catch { /* ignore */ }
    fireTutorialEvent(TUTORIAL_EVENTS.SKIPPED, { source: 'welcome_card' });
    setVisible(false);
  };

  const watch = () => {
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    // Once they've engaged the modal, don't show the welcome card again.
    try { localStorage.setItem(TUTORIAL_STORAGE.DISMISSED, 'true'); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) {
    return open ? (
      <TutorialModal open={open} onClose={handleModalClose} trigger="welcome" />
    ) : null;
  }

  return (
    <>
      <div
        data-testid="tutorial-welcome-card"
        className="mb-6 relative overflow-hidden rounded-2xl border border-[#45C04A]/30 bg-gradient-to-r from-[#12355B]/95 to-[#1565C0]/95 dark:from-[#0B0E14] dark:to-[#131824] p-5 sm:p-6 shadow-card"
      >
        <button
          data-testid="tutorial-welcome-dismiss"
          onClick={dismiss}
          aria-label={t('tutorial.close')}
          className="absolute top-3 right-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pr-8">
          <div className="w-12 h-12 rounded-xl bg-[#45C04A] flex items-center justify-center flex-shrink-0">
            <PlayCircle className="w-6 h-6 text-[#12355B]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-widest text-[#45C04A] font-bold">
              {t('tutorial.welcome.eyebrow')}
            </p>
            <h3 className="mt-0.5 font-primary font-bold text-white text-lg leading-tight">
              {t('tutorial.welcome.title')}
            </h3>
            <p className="mt-1 text-sm text-white/80">
              {t('tutorial.welcome.description')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              data-testid="tutorial-welcome-watch"
              onClick={watch}
              className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl font-semibold"
            >
              {t('tutorial.watchTutorial')}
            </Button>
            <Button
              data-testid="tutorial-welcome-skip"
              onClick={dismiss}
              variant="ghost"
              className="text-white/85 hover:text-white hover:bg-white/10 rounded-xl"
            >
              {t('tutorial.skipForNow')}
            </Button>
          </div>
        </div>
      </div>
      {open && (
        <TutorialModal open={open} onClose={handleModalClose} trigger="welcome" />
      )}
    </>
  );
}
